export function formatTimeInputValue(value: string | null | undefined): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const parts = raw.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return raw;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function addSecondsToTimeValue(time: string, seconds: number): string {
  const from = formatTimeInputValue(time);
  if (!from) return "";
  const [h = 0, m = 0] = from.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
  const total = h * 3600 + m * 60 + seconds;
  const nh = Math.floor((total / 3600) % 24);
  const nm = Math.floor((total % 3600) / 60);
  return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
}

export function parseHoursInput(value: string): number | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const normalized = raw.replace(",", ".");
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export function computeHoursFromTimes(timeFrom: string, timeTo: string): number | null {
  const from = formatTimeInputValue(timeFrom);
  const to = formatTimeInputValue(timeTo);
  if (!from || !to) return null;

  const [fromH = 0, fromM = 0] = from.split(":").map(Number);
  const [toH = 0, toM = 0] = to.split(":").map(Number);
  if (![fromH, fromM, toH, toM].every(Number.isFinite)) return null;

  const daySeconds = 24 * 3600;
  const startSeconds = fromH * 3600 + fromM * 60;
  const endSeconds = toH * 3600 + toM * 60;
  const diffSeconds = (endSeconds - startSeconds + daySeconds) % daySeconds;
  const roundedHours = Math.round((diffSeconds / 3600) * 100) / 100;
  return Number.isFinite(roundedHours) ? roundedHours : null;
}

export function computeTimeToFromHours(timeFrom: string, hours: string): string {
  const from = formatTimeInputValue(timeFrom);
  if (!from) return "";
  const parsedHours = parseHoursInput(hours);
  if (parsedHours == null) return "";
  return addSecondsToTimeValue(from, parsedHours * 3600);
}

export function isSundayIsoDate(dateStr: string): boolean {
  if (!dateStr) return false;
  const d = new Date(`${dateStr}T12:00:00`);
  return !Number.isNaN(d.getTime()) && d.getDay() === 0;
}

export function resolveTimesheetDescriptionByAccountType(
  currentDescription: string,
  accountType?: string | null,
): string {
  const current = currentDescription.trim();
  const isDefaultText = !current || current === "Arbeitszeit" || current === "Technisches Büro";
  if (!isDefaultText) return current;
  return accountType === "full" ? "Technisches Büro" : "Arbeitszeit";
}

export function finalizeTimesheetDescription(
  description: string,
  workDate: string,
  employee: string,
  accountType?: string | null,
): string {
  let effective = description.trim();
  if (!effective && isSundayIsoDate(workDate)) {
    effective = "Sonntagsarbeit";
  }
  if (employee) {
    effective = resolveTimesheetDescriptionByAccountType(effective, accountType);
  }
  return effective;
}

export function isAutoDescription(description: string): boolean {
  const current = description.trim();
  return !current || current === "Arbeitszeit" || current === "Technisches Büro" || current === "Sonntagsarbeit";
}

export function computeLiveDescription(
  description: string,
  workDate: string,
  employee: string,
  accountType?: string | null,
): string {
  if (!isAutoDescription(description)) return description.trim();
  return finalizeTimesheetDescription(description, workDate, employee, accountType);
}

export interface TimeRowFormState {
  person_name: string;
  work_date: string;
  time_from: string;
  time_to: string;
  hours: string;
  comment: string;
}

export function validateTimeRowForm(
  form: TimeRowFormState,
  showTimeRange: boolean,
): { ok: true; hoursDecimal: number } | { ok: false; error: string } {
  const employee = form.person_name.trim();
  const date = form.work_date.trim();
  let timeFrom = showTimeRange ? formatTimeInputValue(form.time_from) : "";
  let timeTo = showTimeRange ? formatTimeInputValue(form.time_to) : "";
  let hours = form.hours.trim();
  const description = form.comment.trim();

  if (showTimeRange) {
    const tolerance = 0.01;
    const parsedHours = parseHoursInput(hours);

    if (timeFrom && parsedHours != null) {
      const expectedTimeTo = computeTimeToFromHours(timeFrom, hours);
      if (expectedTimeTo && (!timeTo || timeTo !== expectedTimeTo)) {
        timeTo = expectedTimeTo;
      }
    }

    const hasTimeFrom = Boolean(timeFrom);
    const hasTimeTo = Boolean(timeTo);

    if (hasTimeFrom !== hasTimeTo) {
      return {
        ok: false,
        error: 'Bitte Zeitspanne vollständig angeben: "von" und "bis" müssen beide gesetzt sein.',
      };
    }

    if (hasTimeFrom && hasTimeTo) {
      const rangeHours = computeHoursFromTimes(timeFrom, timeTo);
      if (rangeHours == null) {
        return { ok: false, error: "Die angegebene Zeitspanne ist ungültig." };
      }
      if (!hours) {
        hours = rangeHours.toFixed(2).replace(".", ",");
      } else {
        if (parsedHours == null) {
          return { ok: false, error: "Bitte eine gültige Stundenanzahl eingeben." };
        }
        if (Math.abs(parsedHours - rangeHours) > tolerance) {
          return {
            ok: false,
            error: "Stunden und Zeitspanne passen nicht zusammen. Bitte Eingaben prüfen.",
          };
        }
      }
    }
  }

  if (!employee && !date && !timeFrom && !timeTo && !hours && !description) {
    return { ok: false, error: "Bitte mindestens ein Feld für die Zeitzeile ausfüllen." };
  }

  const hoursDecimal = parseHoursInput(hours) ?? 0;
  return { ok: true, hoursDecimal };
}
